import {
  clearAccessToken,
  endImpersonation,
  getAccessToken,
  hasSessionHint,
  isImpersonating,
  setAccessToken,
} from '@/lib/auth';
import { ME_QUERY_KEY, queryClient } from '@/lib/query-client';
import * as Sentry from '@sentry/react';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { v4 as uuidv4 } from 'uuid';

export interface RequestConfigExtras {
  idempotencyKey?: string;
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    request_id?: string;
    param?: string;
    metadata?: Record<string, unknown>;
  };
}

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/v1',
  withCredentials: true,
  timeout: 30_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig & RequestConfigExtras) => {
  config.headers = config.headers ?? {};
  config.headers['X-Request-ID'] = uuidv4();
  const token = getAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.idempotencyKey) {
    config.headers['Idempotency-Key'] = config.idempotencyKey;
  }
  return config;
});

// Backend /auth/refresh returns LoginResponse flat (same as /auth/login).
interface RefreshResponse {
  access_token: string;
  token_type: 'Bearer';
  expires_at: string;
}

let refreshPromise: Promise<string | null> | null = null;

export async function refreshSession(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      // Trim trailing slash defensively: VITE_API_URL='.../v1/' would otherwise
      // produce `//auth/refresh`. Bare axios so a 401 here can't recurse.
      const base = (api.defaults.baseURL ?? '').replace(/\/$/, '');
      const resp = await axios.post<RefreshResponse>(
        `${base}/auth/refresh`,
        {},
        { withCredentials: true, timeout: 15_000 },
      );
      const token = resp.data?.access_token ?? null;
      if (token) setAccessToken(token);
      return token;
    } catch {
      // Token & session are gone — clear local state so ProtectedRoute /
      // useAuth see isAuthenticated=false on next render and redirect to
      // /sign-in.
      //
      // CRITICAL: only call removeQueries if there was actually cached `me`
      // data (i.e. user was previously signed in). On a fresh visit by an
      // anonymous user, the /me query is *in flight* when this runs, so
      // removeQueries cancels it AND triggers an immediate re-fetch on the
      // observing component — which 401s, retries refresh, removes the
      // query again → infinite loop. Symptom: white-screen spinner forever
      // on / for first-time visitors. Fixed 2026-05-22.
      clearAccessToken();
      const existing = queryClient.getQueryData(ME_QUERY_KEY);
      if (existing) {
        queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
      }
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function isRefreshableAuthError(error: AxiosError<ApiErrorBody>): boolean {
  if (error.response?.status !== 401) return false;
  // Require explicit code from our backend envelope — never "any 401 with no
  // body". A code-less 401 from a misconfigured proxy or third-party host
  // should NOT trigger refresh + retry (which could leak the new bearer).
  // token_missing covers the page-reload case: the access token lives in
  // memory and is gone after F5, but the httpOnly refresh cookie survives.
  const code = error.response?.data?.error?.code;
  return (
    code === 'auth.token_expired' || code === 'auth.token_invalid' || code === 'auth.token_missing'
  );
}

// Fired when an impersonation token expires (401) so React-land can clear the
// query cache, restore the admin session and navigate. Dispatched on `window`
// because the interceptor lives outside the component tree.
export const IMPERSONATION_EXPIRED_EVENT = 'dagsight:impersonation-expired';

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError<ApiErrorBody>) => {
    const status = error.response?.status;
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;

    // While impersonating, a 401 means the impersonation token expired/revoked.
    // We must NOT run the normal refresh flow: the httpOnly refresh cookie still
    // belongs to the ADMIN, so refreshing would silently mint an admin token and
    // retry the request as the admin — a context leak. Instead, synchronously
    // restore the admin session (token swap-back) and notify React-land to clean
    // up. Re-reject so the originating call still sees the 401.
    if (isImpersonating() && isRefreshableAuthError(error)) {
      endImpersonation();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(IMPERSONATION_EXPIRED_EVENT));
      }
      return Promise.reject(error);
    }

    // `hasSessionHint()`: only probe POST /auth/refresh for visitors who have
    // actually had a session. A cold anonymous visitor has no refresh cookie,
    // so the probe just 401s in the console — skip it. F5 of a signed-in user
    // still recovers (the hint persists in localStorage across reloads).
    if (
      original &&
      !original._retry &&
      isRefreshableAuthError(error) &&
      hasSessionHint()
    ) {
      // Never try to refresh the refresh endpoint itself.
      const url = original.url ?? '';
      if (!url.includes('/auth/refresh') && !url.includes('/auth/login')) {
        original._retry = true;
        const newToken = await refreshSession();
        if (newToken) {
          original.headers = original.headers ?? {};
          original.headers.Authorization = `Bearer ${newToken}`;
          return api.request(original);
        }
      }
    }

    if (status && status >= 500) {
      Sentry.captureException(error, {
        tags: { source: 'api', status: String(status) },
        extra: { url: original?.url, method: original?.method },
      });
    }

    return Promise.reject(error);
  },
);

export function extractApiError(err: unknown): ApiErrorBody['error'] | null {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error) return body.error;
  }
  return null;
}
